import { Injectable, inject } from '@angular/core';
import { MessageService } from 'primeng/api';
import { QuestionView, QuestionType, QuestionTypeMapper, generateGuid } from '../models';
import { FormElementApiService } from './form-element-api.service';
import { CreateFormElementBatchItemDto, FormElementType } from '@formrails/shared/data-access';
import { FormStore } from '../store/form.store';

/**
 * Service to handle saving reportable form elements to the form element library.
 * Converts QuestionView objects to CreateFormElementBatchItemDto format and manages
 * the batch save lifecycle.
 */
@Injectable({
  providedIn: 'root'
})
export class ReportableElementsService {
  private formStore = inject(FormStore);
  private formElementApiService = inject(FormElementApiService);

  /**
   * Save reportable form elements to the form element library via batch endpoint.
   */
  saveReportableElements(
    reportableQuestions: QuestionView[],
    messageService: MessageService,
    onElementsSaved: (questionIds: string[]) => void
  ): void {
    if (reportableQuestions.length === 0) return;

    // One unusable element would fail the whole batch, so anything that cannot
    // be expressed in the library's terms is left behind rather than sent.
    const savable = reportableQuestions
      .map(question => ({ question, type: this.mapQuestionTypeToFormElementType(question.questionType || question.type) }))
      .filter(({ question, type }) => {
        if (!type) {
          console.warn(`Reportable elements: "${question.labelText}" has no library type (${question.questionType ?? question.type}); skipped`);
          return false;
        }
        // The API rejects a batch whose choice-based element carries no options.
        if (this.requiresOptions(type) && this.mapOptionsToDto(question.options).length === 0) {
          console.warn(`Reportable elements: "${question.labelText}" is a ${type} with no options; skipped`);
          return false;
        }
        return true;
      });

    if (savable.length === 0) return;

    const batchItems: CreateFormElementBatchItemDto[] = savable.map(({ question, type }) => ({
      // The question's own id: the template records it against the question, so
      // sending it keeps the library element and the question as one thing.
      id: question.id,
      labelText: question.labelText || '',
      additionalInfo: question.additionalInfo || '',
      type: type as FormElementType,
      isRequired: question.isRequired || false,
      helperText: question.helperText || '',
      tooltip: question.tooltip || '',
      placeholder: question.placeholder || '',
      semanticTags: question.semanticTags || [],
      options: this.mapOptionsToDto(question.options),
      validators: question.validators || {}
    }));

    this.formElementApiService.createFormElementsBatch(batchItems).subscribe({
      next: () => {
        console.log('Reportable elements saved successfully');
        onElementsSaved(savable.map(({ question }) => question.id));
      },
      error: (error) => {
        console.error('Failed to save reportable elements:', error);
        messageService.add({
          severity: 'warn',
          summary: 'Reportable Elements',
          detail: 'Form saved but failed to add elements to library. They will be saved on next save.',
          life: 5000
        });
      }
    });
  }

  /**
   * Mark questions as saved (no longer new elements).
   * Prevents them from being sent again on subsequent saves.
   */
  markElementsAsSaved(
    questionIds: string[],
    getQuestions: () => QuestionView[],
    setQuestions: (questions: QuestionView[]) => void
  ): void {
    const currentQuestions = getQuestions();
    const updatedQuestions = currentQuestions.map(q => {
      if (questionIds.includes(q.id)) {
        return { ...q, isNewElement: false };
      }
      return q;
    });
    setQuestions(updatedQuestions);

    questionIds.forEach(id => {
      const question = updatedQuestions.find(q => q.id === id);
      if (question) {
        this.formStore.updateQuestion(question);
      }
    });
  }

  /**
   * Map a question's type to the library's FormElementType.
   *
   * Only the names the API's enum actually declares are valid here — it has
   * Checkbox and Dropdown, not the builder's CheckboxList and DropdownList —
   * and a type with no counterpart returns null so the element is left out
   * rather than silently filed as something it is not.
   */
  private mapQuestionTypeToFormElementType(type: QuestionType | string | undefined): FormElementType | null {
    if (!type) return null;

    // Keyed on lower case: QuestionType's values are lower case ('textbox'),
    // while a question loaded from the API carries the wire name ('Input').
    const typeMap: Record<string, FormElementType> = {
      textbox: 'Textbox',
      input: 'Textbox',
      textarea: 'TextArea',
      email: 'Email',
      date: 'Date',
      tel: 'Tel',
      radio: 'Radio',
      checkbox: 'Checkbox',
      checkboxlist: 'Checkbox',
      toggle: 'Checkbox',
      confirmation: 'Confirmation',
      dropdown: 'Dropdown',
      dropdownlist: 'Dropdown',
      dynamicdropdown: 'Dropdown',
      title: 'Title',
      helpertext: 'HelperText'
      // Section is deliberately absent: it holds no response to report on.
    };

    return typeMap[String(type).toLowerCase()] ?? null;
  }

  /** Choice-based elements must carry at least one option; the API validates this. */
  private requiresOptions(type: FormElementType): boolean {
    return type === 'Checkbox' || type === 'Dropdown' || type === 'Radio';
  }

  /** Map question options to QuestionResponseOptionDto format */
  private mapOptionsToDto(options: QuestionView['options']): { id: string; order: number; text: string; isCustomOption: boolean }[] {
    if (!options || !Array.isArray(options)) return [];
    return options.map((opt, index) => {
      let text: string;
      let isCustomOption = false;
      if (typeof opt === 'string') {
        text = opt;
      } else {
        text = opt.text || '';
        isCustomOption = opt.isCustomOption || false;
      }
      return { id: generateGuid(), order: index, text, isCustomOption };
    });
  }
} 
